import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React from 'react';

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#178B8E',
          color: '#FFFFFF',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
            📋 Terms & Conditions
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 4, color: '#5A5A5A' }}>
          These Terms & Conditions ("Terms") govern your use of the Happy Paws platform. By using
          the app, you agree to comply with these terms.
        </Typography>

        {/* Section 1 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          By accessing or using Happy Paws, you agree to be legally bound by these Terms and our
          Privacy Policy.
        </Typography>

        {/* Section 2 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          2. User Roles
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#5A5A5A' }}>
          There are two types of users:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              <strong>Pet Owners:</strong> who book pet care services.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              <strong>Pet Sitters:</strong> who offer services such as sitting, walking, or
              training.
            </Typography>
          </li>
        </Box>

        {/* Section 3 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          3. Account Eligibility
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          Users must be at least 18 years old and provide accurate information. We reserve the right
          to suspend or terminate accounts that violate these terms.
        </Typography>

        {/* Section 4 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          4. Bookings & Payments
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Owners are charged in advance when booking.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Payments are processed securely via Stripe.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Sitters are paid out within a few business days after service completion, subject to
              Stripe processing times.
            </Typography>
          </li>
        </Box>

        {/* Section 5 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          5. Cancellations & Refunds
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          Sitters may define their own cancellation policy (Flexible, Moderate, or Strict). Refunds
          depend on the sitter's policy and the time of cancellation.
        </Typography>

        {/* Section 6 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          6. User Responsibilities
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Sitters must provide safe and responsible care for pets.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Owners must share accurate pet information and care instructions.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Users agree to resolve issues respectfully and avoid abuse or harassment.
            </Typography>
          </li>
        </Box>

        {/* Section 7 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          7. Prohibited Activities
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Fraudulent or fake bookings
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Off-platform payments or circumvention
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Harmful or abusive behavior
            </Typography>
          </li>
        </Box>

        {/* Section 8 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          Happy Paws operates as a marketplace and intermediary platform. We are not a party to the
          service agreement between pet owners and sitters, and we are not responsible for damages,
          pet incidents, injuries, or losses arising from services booked through the platform.
          Users engage with each other at their own discretion and risk.
        </Typography>

        {/* Section 9 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          9. Termination
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          We may suspend or terminate user accounts that violate these terms without prior notice.
        </Typography>

        {/* Section 10 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          10. Changes to Terms
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          We may update these terms from time to time. Updates will be posted on this page.
        </Typography>

        {/* Section 11 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          11. Governing Law
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          These terms are governed by the laws of the Republic of Serbia, unless otherwise required
          by applicable law in your jurisdiction.
        </Typography>

        {/* Section 12 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          12. Contact
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          If you have questions about these terms, contact us at{' '}
          <a
            href="mailto:support@happypaws.com"
            style={{ color: '#178B8E', textDecoration: 'none' }}
          >
            support@happypaws.com
          </a>
          .
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#178B8E',
            color: '#FFFFFF',
            px: 4,
            '&:hover': {
              backgroundColor: '#0F6164',
            },
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsModal;
